/**
 * Browser-run rendition backfill (JARVIS-122). Runs on the debug route, in the
 * same tab that uploads artwork, so it reuses the exact canvas → JPEG → Storage
 * path as `ArtworkUploadModal`. A Node script is not an option: Node has no
 * image decoder here without adding `sharp`, a new dependency.
 *
 * Idempotent — a candidate whose thumb AND med already exist (`getMetadata`
 * succeeds for both) is counted as `existing` and never rewritten; a
 * half-written pair gets only its missing tier written and counts as `written`
 * — and abortable between items. Any repair attempt invalidates both tier
 * paths so the session cache matches Storage whatever an earlier pass left.
 */
import * as Sentry from "@sentry/react"
import { compact, forEach } from "lodash"
import { useCallback, useRef, useState } from "react"

import Firestore from "#classes/Firestore"
import {
  BACKFILL_OUTCOME,
  type BackfillCandidate,
  type BackfillResult,
  type BackfillTally,
  tallyBackfill,
} from "#utils/artworkRenditionBackfill"
import {
  RENDITION_TIER,
  type RenditionTier,
} from "#utils/artworkRenditionHelpers"
import { uploadArtworkRenditionTiers } from "#utils/imageRenditions"

import { invalidateRenditionUrl } from "./useRenditionUrl"

export const BACKFILL_STATUS = {
  IDLE: "idle",
  RUNNING: "running",
  DONE: "done",
  ABORTED: "aborted",
} as const

export type BackfillStatus =
  (typeof BACKFILL_STATUS)[keyof typeof BACKFILL_STATUS]

/** Two in flight keeps the emulator and a laptop's canvas budget comfortable. */
export const BACKFILL_CONCURRENCY = 2

const describeError = (error: unknown): string =>
  error instanceof Error ? error.message : String(error)

export const processBackfillCandidate = async (
  candidate: BackfillCandidate
): Promise<BackfillResult> => {
  const storage = Firestore.getInstance()
  const pathByTier: Record<RenditionTier, string> = {
    [RENDITION_TIER.THUMB]: candidate.thumbPath,
    [RENDITION_TIER.MED]: candidate.medPath,
  }
  try {
    // Both tiers must exist to count as done. Checking only the thumb would
    // permanently strand a piece whose thumb upload succeeded and whose med
    // upload failed, leaving strip view on the full-size original forever.
    const [existingThumb, existingMed] = await Promise.all([
      storage.getMetadataOrNull(candidate.thumbPath),
      storage.getMetadataOrNull(candidate.medPath),
    ])
    const missingTiers = compact([
      existingThumb ? null : RENDITION_TIER.THUMB,
      existingMed ? null : RENDITION_TIER.MED,
    ])
    if (missingTiers.length === 0) {
      return { candidate, outcome: BACKFILL_OUTCOME.EXISTING }
    }
    // Repair only what is missing. Re-uploading a tier that already exists
    // would replace a working object under a fresh download token and
    // needlessly invalidate every URL already resolved for it.
    try {
      const original = await storage.getBlob(candidate.objectPath)
      await uploadArtworkRenditionTiers(
        original,
        candidate.objectPath,
        missingTiers,
        {
          studentId: candidate.studentId,
          classId: candidate.artwork.classId,
          artworkId: candidate.artworkId,
          backfill: "true",
        }
      )
    } finally {
      // Invalidate EVERY tier after a repair attempt, not only the ones this
      // pass uploaded, and even when the upload failed. A tier that already
      // existed may have been written by an earlier pass that died before
      // invalidating it (Promise.all lands one tier and rejects on the other),
      // so the session cache still holds a pre-write miss for it and the wall
      // sits on the full-size original. Invalidation never touches Storage;
      // it only makes mounted hooks re-probe, and a genuine miss re-confirms.
      forEach(pathByTier, (path) => invalidateRenditionUrl(path))
    }
    return { candidate, outcome: BACKFILL_OUTCOME.WRITTEN }
  } catch (error) {
    Sentry.captureException(error)
    return {
      candidate,
      outcome: BACKFILL_OUTCOME.FAILED,
      error: describeError(error),
    }
  }
}

interface BackfillState {
  status: BackfillStatus
  total: number
  results: BackfillResult[]
}

const INITIAL_STATE: BackfillState = {
  status: BACKFILL_STATUS.IDLE,
  total: 0,
  results: [],
}

export interface RenditionBackfillController {
  status: BackfillStatus
  results: BackfillResult[]
  tally: BackfillTally
  start: (candidates: readonly BackfillCandidate[]) => Promise<void>
  abort: () => void
  reset: () => void
}

export const useRenditionBackfill = (): RenditionBackfillController => {
  const [state, setState] = useState<BackfillState>(INITIAL_STATE)
  const abortRef = useRef(false)
  const runningRef = useRef(false)

  const abort = useCallback(() => {
    abortRef.current = true
  }, [])

  const reset = useCallback(() => {
    if (runningRef.current) return
    abortRef.current = false
    setState(INITIAL_STATE)
  }, [])

  const start = useCallback(
    async (candidates: readonly BackfillCandidate[]) => {
      if (runningRef.current) return
      runningRef.current = true
      abortRef.current = false
      setState({
        status: BACKFILL_STATUS.RUNNING,
        total: candidates.length,
        results: [],
      })

      let cursor = 0
      const worker = async () => {
        while (!abortRef.current && cursor < candidates.length) {
          const candidate = candidates[cursor]
          cursor += 1
          const result = await processBackfillCandidate(candidate)
          setState((prev) => ({ ...prev, results: [...prev.results, result] }))
        }
      }

      const workers = Array.from(
        { length: Math.min(BACKFILL_CONCURRENCY, candidates.length) },
        () => worker()
      )
      await Promise.all(workers)

      runningRef.current = false
      setState((prev) => ({
        ...prev,
        status: abortRef.current
          ? BACKFILL_STATUS.ABORTED
          : BACKFILL_STATUS.DONE,
      }))
    },
    []
  )

  return {
    status: state.status,
    results: state.results,
    tally: tallyBackfill(state.results, state.total),
    start,
    abort,
    reset,
  }
}
